// KundenverbindungDialog.tsx — Popup zum Bearbeiten EINER Kundenverbindung
// (User 28.09.2026). Geöffnet aus der Dashboard-Karte „Kundenverbindungen";
// löst die frühere Ansicht in der Kontakttabelle ab (KundenverbindungenView).
//
// Kann: Name ändern, Mitglieder öffnen/entfernen/hinzufügen, mit einer
// anderen Kundenverbindung zusammenführen (→ bestehende Merge-Seite) und die
// Kundenverbindung löschen (Soft-Delete; die Kontakte bleiben).

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useNavigate } from "react-router";
import { I } from "./kvpIcons";
import { RelationContactPicker } from "./RelationContactPicker";
import { apiFetch } from "../../utils/apiFetch";
import { contactTypeLabel } from "../../utils/contactTypeLabel";
import "./contactRedesign.css";
import { bestaetigen } from "./bestaetigen";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

export type KundenverbindungKurz = {
  id: number;
  name: string;
  memberCount: number;
  ansprechpartnerName?: string | null;
};
type Member = { id: number; name: string; typ: string };

export function KundenverbindungDialog({
  kv,
  alle,
  onClose,
  onGeaendert,
}: {
  kv: KundenverbindungKurz;
  /** Alle Kundenverbindungen — Auswahl fürs Zusammenführen. */
  alle: KundenverbindungKurz[];
  onClose: () => void;
  /** Name/Mitglieder geändert oder gelöscht (`null`) — die Karte zieht nach. */
  onGeaendert: (kv: KundenverbindungKurz | null) => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = React.useState(kv.name);
  const [gespeicherterName, setGespeicherterName] = React.useState(kv.name);
  const [mitglieder, setMitglieder] = React.useState<Member[] | null>(null);
  const [neu, setNeu] = React.useState<{ id: number } | null>(null);
  const [zusammenMit, setZusammenMit] = React.useState("");
  const [arbeitet, setArbeitet] = React.useState(false);
  const [fehler, setFehler] = React.useState("");

  const mitgliederLaden = React.useCallback(async () => {
    try {
      const { data } = await apiFetch<Member[]>(`contactgroup/${kv.id}/contacts`);
      const liste = Array.isArray(data) ? data : [];
      setMitglieder(liste);
      return liste;
    } catch {
      setMitglieder([]);
      return [];
    }
  }, [kv.id]);
  React.useEffect(() => {
    void mitgliederLaden();
  }, [mitgliederLaden]);

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    alleFenster.addEventListener("keydown", esc);
    return () => alleFenster.removeEventListener("keydown", esc);
  }, [onClose]);

  const ausfuehren = async (fn: () => Promise<void>, fehlertext: string) => {
    setArbeitet(true);
    setFehler("");
    try {
      await fn();
    } catch {
      setFehler(fehlertext);
    } finally {
      setArbeitet(false);
    }
  };

  const nameSpeichern = () =>
    ausfuehren(async () => {
      const n = name.trim();
      await apiFetch(`contactgroup/${kv.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: n }),
      });
      setGespeicherterName(n);
      onGeaendert({ ...kv, name: n, memberCount: mitglieder?.length ?? kv.memberCount });
    }, "Name konnte nicht gespeichert werden.");

  const hinzufuegen = () =>
    ausfuehren(async () => {
      if (!neu) return;
      await apiFetch(`contactgroup/${kv.id}/contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactId: neu.id }),
      });
      setNeu(null);
      const liste = await mitgliederLaden();
      onGeaendert({ ...kv, name: gespeicherterName, memberCount: liste.length });
    }, "Kontakt konnte nicht hinzugefügt werden.");

  const entfernen = (id: number) =>
    ausfuehren(async () => {
      await apiFetch(`contactgroup/${kv.id}/contacts/${id}`, { method: "DELETE" });
      const liste = await mitgliederLaden();
      onGeaendert({ ...kv, name: gespeicherterName, memberCount: liste.length });
    }, "Kontakt konnte nicht entfernt werden.");

  const loeschen = async () => {
    const ok = await bestaetigen({
      titel: "Kundenverbindung löschen?",
      text: `„${gespeicherterName}" wird gelöscht. Die zugeordneten Kontakte bleiben erhalten.`,
      ja: "Löschen",
      ton: "danger",
    });
    if (!ok) return;
    void ausfuehren(async () => {
      await apiFetch(`contactgroup/${kv.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      onGeaendert(null);
      onClose();
    }, "Kundenverbindung konnte nicht gelöscht werden.");
  };

  const nameGeaendert = !!name.trim() && name.trim() !== gespeicherterName;
  const andere = alle.filter((x) => x.id !== kv.id);

  return createPortal(
    <div className="kvp-rd kvp-rd-scrim" onClick={onClose}>
      <div
        className="dm kvd-huelle"
        role="dialog"
        aria-modal="true"
        aria-label={`Kundenverbindung ${gespeicherterName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">Kundenverbindung</div>
              <h2>{gespeicherterName}</h2>
              {kv.ansprechpartnerName ? (
                <div className="kvd-verantw">Verantwortlich: {kv.ansprechpartnerName}</div>
              ) : null}
            </div>
            <button type="button" className="dm-x" aria-label="Schließen" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
            </button>
          </div>
        </div>

        <div className="dm-body kvv-edit">
          <div className="kvv-edit-grouptitle">Name</div>
          <div className="kvv-name-row">
            <input
              type="text"
              className="kvv-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && nameGeaendert && void nameSpeichern()}
              placeholder="Name der Kundenverbindung"
            />
            <button type="button" className="rd-pop-btn primary" onClick={() => void nameSpeichern()} disabled={arbeitet || !nameGeaendert}>
              Speichern
            </button>
          </div>

          <div className="kvv-edit-grouptitle">Mitglieder{mitglieder ? ` · ${mitglieder.length}` : ""}</div>
          {mitglieder === null ? (
            <div className="kvv-msg">Lädt …</div>
          ) : mitglieder.length === 0 ? (
            <div className="kvv-msg">Keine Kontakte in dieser Kundenverbindung.</div>
          ) : (
            mitglieder.map((m) => (
              <div key={m.id} className="kvv-edit-member">
                <button
                  type="button"
                  className="kvd-member-link"
                  title="Kontakt öffnen"
                  onClick={() => {
                    onClose();
                    navigate(`/kontakte/edit/${m.id}`);
                  }}
                >
                  <span className="kvv-edit-member-name">{m.name}</span>
                  <span className="kvv-member-typ">{contactTypeLabel(m.typ)}</span>
                </button>
                <button type="button" className="kvv-edit-remove" onClick={() => void entfernen(m.id)} disabled={arbeitet}>
                  {I.trash} entfernen
                </button>
              </div>
            ))
          )}

          <div className="kvv-edit-sep">
            <div className="kvv-edit-grouptitle" style={{ marginTop: 0 }}>Kontakt hinzufügen</div>
            <RelationContactPicker
              defaultType="Person"
              selected={neu}
              onSelect={(c) => setNeu(c)}
              excludeContactIds={(mitglieder ?? []).map((m) => m.id)}
            />
            <button type="button" className="rd-pop-btn primary kvv-block-btn" onClick={() => void hinzufuegen()} disabled={!neu || arbeitet}>
              Hinzufügen
            </button>
          </div>

          {andere.length > 0 && (
            <div className="kvv-edit-sep">
              <div className="kvv-edit-grouptitle" style={{ marginTop: 0 }}>Zusammenführen mit</div>
              <div className="kvv-name-row">
                <select className="kvv-input" value={zusammenMit} onChange={(e) => setZusammenMit(e.target.value)}>
                  <option value="">Kundenverbindung wählen …</option>
                  {andere.map((x) => (
                    <option key={x.id} value={String(x.id)}>
                      {x.name} ({x.memberCount})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="rd-pop-btn"
                  disabled={!zusammenMit}
                  onClick={() => {
                    onClose();
                    navigate(`/contactgroups/merge?clusterIds=${kv.id},${zusammenMit}`);
                  }}
                >
                  {I.merge} Zusammenführen
                </button>
              </div>
            </div>
          )}

          {fehler && <div className="kvv-edit-err">{fehler}</div>}
        </div>

        <div className="dm-foot kvd-foot">
          <button type="button" className="tbar-del" onClick={loeschen} disabled={arbeitet}>
            {I.trash}Löschen
          </button>
          <span style={{ flex: 1 }} />
          <button type="button" className="rd-pop-btn primary" onClick={onClose}>
            Fertig
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
